import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
    Avatar,
    Box,
    Button,
    Chip,
    Collapse,
    Container,
    IconButton,
    Paper,
    Stack,
    TableCell,
    TableRow, Typography
} from '@mui/material';
import { useStoreState } from 'pullstate';
import React, { useCallback, useEffect, useState } from 'react';
import Strengths from '../../components/Strengths.jsx';
import { AppStore } from '../../stores/AppStore.js';
import { getPeopleBuilderStatus } from '../../utils/getPeopleBuilderStatus.js';
import CheckIns from '../checkins/CheckIns.jsx';
import RequiredCheckIn from '../checkins/RequiredCheckIn.jsx';
import Goals from '../goals/Goals.jsx';

const DepartmentPeopleBuilderRow = React.forwardRef((props, ref) => {
    const { peopleBuilders, employees, goals } = useStoreState(AppStore)
    const {
        playerId, peopleBuilder, setTitle, title, checkIns,
        handleScroll, strengths, setCurrentUser, setBreadcrumbsPb
    } = props;
    const [open, setOpen] = React.useState(false);
    const [userStrengths, setUserStrengths] = useState([])
    const [pbClosed, setPbClosed] = useState(false)
    const [dateCreation, setDateCreation] = useState('')
    const [filterGoalsByUser, setFilterGoalsByUser] = useState([])
    const [playersCoachPlayers, setPlayersCoachPlayers] = useState([])
    const [rowStatus, setRowStatus] = useState({ status: '', color: '' })
    const [pbData, setPbData] = useState({
        year: null,
        status: null,
        qty_goals: null,
        creating: null,
        last_checkin: null
    })

    useEffect(() => {
        const playersCoachPlayers = peopleBuilders.filter(pb => {
            return pb.player.coach === peopleBuilder.player.full_name && pb.year === peopleBuilder.year
        })
        setPlayersCoachPlayers(playersCoachPlayers)
    }, [])

    const getDateCreation = useCallback(() => {
        if (filterGoalsByUser.length === 0) return null;
        const mostRecentGoal = filterGoalsByUser?.reduce((mostRecent, current) => {
            return new Date(current.createdAt) > new Date(mostRecent.createdAt) ? current : mostRecent;
        });

        setDateCreation(mostRecentGoal.createdAt.split('T')[0])

    }, [goals, rowStatus, checkIns])

    const updateStatus = useCallback(() => {
        if (!goals) return;
        const usersGoals = goals.filter(goal => {
            return goal?.people_builder?.player_id === playerId &&
                goal?.people_builder?.year === peopleBuilder?.year
        })
        setFilterGoalsByUser(usersGoals)
        setPbData(prevStates => ({
            ...prevStates,
            qty_goals: usersGoals.length
        }))
        const status = getPeopleBuilderStatus(usersGoals)
        
        setRowStatus(status)
    }, [goals, checkIns])

    const checkPbClosed = useCallback(() => {
        setPbClosed(peopleBuilder.status !== 'active')
    }, [peopleBuilder])

    const findMostRecentCheckin = (checkins) => {
        if (checkins?.length === 0) return null;

        const checinsFilteredByYear = checkins?.filter(checkin => new Date(checkin.check_in_date).getFullYear() === peopleBuilder?.year)

        if (checinsFilteredByYear?.length === 0) return null;

        return checinsFilteredByYear?.reduce((mostRecent, current) => {
            return new Date(current.check_in_date) > new Date(mostRecent.check_in_date) ? current : mostRecent;
        });
    };

    const buildData = useCallback(() => {

        const userCheckins = checkIns?.filter(checkin => checkin.player_id === playerId)
        const mostRecentCheckin = findMostRecentCheckin(userCheckins);
        getDateCreation()
        setPbData(prevStates => ({
            ...prevStates,
            qty_goals: filterGoalsByUser.length,
            status: filterGoalsByUser.length > 0 ? rowStatus : 'No Goals',
            creating: dateCreation,
            year: peopleBuilder?.year,
            last_checkin: mostRecentCheckin?.check_in_date.split('T')[0]
        }))
    }, [peopleBuilder, checkIns, rowStatus, dateCreation, filterGoalsByUser])

    useEffect(() => {
        buildData()
        updateStatus()
    }, [goals, checkIns])

    const openRow = useCallback(() => {
        setOpen(localStorage.getItem('playerIdClickedPB') === peopleBuilder?.player.id + peopleBuilder?.year)
    }, [open])

    const getUserStrengths = useCallback(async () => {
        const strengthFiltered = strengths.filter(employee => employee.user_id == playerId)
        setUserStrengths(strengthFiltered)
    }, [strengths])

    const handleOpenBreadcrumbs = () => {
        const currentUser = employees.find(employee => employee.id === peopleBuilder?.player_id);
        setCurrentUser(currentUser)
        setBreadcrumbsPb((prev) => ([...prev, peopleBuilder?.player?.full_name]));
    }

    useEffect(() => {
        getUserStrengths()
    }, [])

    useEffect(() => {
        checkPbClosed()
        openRow()
    }, [])

    useEffect(() => {
        checkPbClosed()
    }, [pbClosed])

    return (
        <React.Fragment >
            <TableRow
                hover
                ref={ref}
                sx={{ '& > *': { 'borderBottom': 'unset' }, cursor: 'pointer' }}
                onClick={(e) => {
                    localStorage.setItem('playerIdClickedPB', peopleBuilder?.player.id + peopleBuilder?.year);
                    open && localStorage.removeItem('playerIdClickedPB');
                    handleScroll()
                    setOpen(!open)
                }}
            >
                <TableCell sx={{ width: '1rem', textAlign: 'center' }}>
                    <Stack direction='row' justifyContent='space-between' alignItems='center'>
                        <Stack direction='row' justifyContent='flex-start' alignItems='center' gap={1}>
                            <Stack direction='row' justifyContent='space-around' alignItems='center' gap={20}>
                                <Stack direction='row' textAlign='left' alignItems='center' justifyContent='flex-start' gap={1} >
                                    <IconButton
                                        aria-label="expand row"
                                        size="small"
                                        onClick={() => setOpen(!open)}
                                    >
                                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    </IconButton>
                                    <Avatar src={peopleBuilder?.player.photo_url} />
                                    <Typography sx={{ width: '200px' }} variant='body1'>{peopleBuilder?.player.full_name}</Typography>
                                </Stack>
                                <Box sx={{ width: '200px', textAlign: 'left' }}>
                                    <Chip sx={{ backgroundColor: rowStatus.color, color: 'black' }} label={rowStatus.status} />
                                </Box>
                                <Typography>Total Goals: {pbData.qty_goals}</Typography>
                            </Stack>
                        </Stack>
                        {playersCoachPlayers.length > 0 && (
                            <Typography mr={2} variant='body1'>
                                <Button
                                    size='small'
                                    variant='text'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenBreadcrumbs();
                                    }}
                                >
                                    {`${peopleBuilder?.player.full_name}'s team (${playersCoachPlayers.length})`}
                                    <ArrowForwardIosIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                                </Button>
                            </Typography>
                        )}
                    </Stack>
                </TableCell>
            </TableRow>
            <TableRow hover >
                <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={3}
                >
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        {userStrengths.length ? (
                            <>
                                <Container maxWidth='md' sx={{ mt: 2 }}>
                                    <Strengths strengths={userStrengths} employeeId={playerId} styles={{ marginRight: 10 }} />
                                </Container>
                            </>
                        ) : ('')}
                        <Paper sx={{ m: 2, p: 2 }}>
                            <Typography
                                variant="h6"
                                textAlign='center'
                                gutterBottom
                                component="div"
                            >
                                Goals
                            </Typography>
                            <Goals pbClosed={pbClosed} peopleBuilder={peopleBuilder} playerId={playerId} />
                        </Paper>
                        <CheckIns pbClosed={pbClosed} peopleBuilder={peopleBuilder} playerId={playerId} />
                        <RequiredCheckIn pbClosed={pbClosed} setTitle={setTitle} title={title} peopleBuilder={peopleBuilder} playerId={playerId} />
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
})

export default DepartmentPeopleBuilderRow